import { Routes } from '@angular/router';
import { FacturaOwnerTableComponent } from '@features/factura/components/factura-owner-table.component';
import { EntityType, Role } from '@gen-common/enums';
import { environment } from 'src/environments/environment';

import { AppLayoutComponent } from './app-layout.component';
import { PermissionGuard } from './auth/auth-permission.guard';
import { roleGuard } from './auth/auth-role.guard';
import { RootRedirectComponent } from './auth/root-redirect.component';
import { UnauthComponent } from './auth/unauth.component';
import { HomeComponent } from './features/home/home.component';
import { SystemConsistencyComponent } from './features/system-consistency/system-consistency.component';

export const routes: Routes = [
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.routes').then((m) => m.routes)
  },
  { path: 'unauthorized', component: UnauthComponent },
  {
    path: '',
    component: AppLayoutComponent,
    children: [
      { path: 'home', component: HomeComponent, data: { pageTitle: 'home.pageTitle' } },
      {
        path: 'owner',
        loadChildren: () => import('./routes/owner.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'owner.pageTitle', entity: EntityType.OWNER }
      },
      {
        path: 'guest',
        loadChildren: () => import('./routes/guest.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'guest.pageTitle', entity: EntityType.GUEST }
      },
      {
        path: 'supplier',
        loadChildren: () => import('./routes/supplier.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'supplier.pageTitle', entity: EntityType.SUPPLIER }
      },
      {
        path: 'stafflocation',
        loadChildren: () => import('./routes/stafflocation.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'stafflocation.pageTitle', entity: EntityType.STAFFLOCATION }
      },
      {
        path: 'inventoryitem',
        loadChildren: () => import('./routes/inventoryitem.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'inventoryitem.pageTitle', entity: EntityType.INVENTORYITEM }
      },
      {
        path: 'inventorytransaction',
        loadChildren: () => import('./routes/inventorytransaction.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'inventorytransaction.pageTitle', entity: EntityType.INVENTORYTRANSACTION }
      },

      {
        path: 'property',
        loadChildren: () => import('./routes/property.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'property.pageTitle', entity: EntityType.PROPERTY }
      },
      {
        path: 'reservation',
        loadChildren: () => import('./routes/reservation.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'reservation.pageTitle', entity: EntityType.RESERVATION }
      },
      {
        path: 'payment',
        loadChildren: () => import('./routes/payment.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'payment.pageTitle', entity: EntityType.PAYMENT }
      },
      {
        path: 'cashtransfer',
        loadChildren: () => import('./routes/cashtransfer.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'cashtransfer.pageTitle', entity: EntityType.CASHTRANSFER }
      },
      {
        path: 'email',
        loadChildren: () => import('./routes/email.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'menu.emails', entity: EntityType.EMAIL }
      },
      {
        path: 'employee',
        loadChildren: () => import('./routes/employee.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'employee.pageTitle', entity: EntityType.EMPLOYEE }
      },
      { path: 'employeesalary/:id', redirectTo: 'employee/salary/:id' },
      {
        path: 'report',
        loadChildren: () => import('./routes/report.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'report.pageTitle', entity: EntityType.REPORT }
      },
      {
        path: 'cleaning',
        loadChildren: () => import('./routes/cleaning.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'cleaning.pageTitle', entity: EntityType.CLEANING }
      },
      {
        path: 'task',
        loadChildren: () => import('./routes/task.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'task.pageTitle', entity: EntityType.TASK }
      },
      {
        path: 'issue',
        loadChildren: () => import('./routes/issue.routes').then((m) => m.routes),
        data: { pageTitle: 'issue.pageTitle' }
      },
      {
        path: 'cleaning-mobile',
        loadChildren: () => import('./routes/cleaning-mobile.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'cleaning.pageTitle', entity: EntityType.CLEANING }
      },
      {
        path: 'task-mobile',
        loadChildren: () => import('./routes/task-mobile.routes').then((m) => m.routes),
        canActivate: [roleGuard([Role.TECHNICIAN]), PermissionGuard],
        data: { pageTitle: 'task.pageTitle', entity: EntityType.TASK }
      },
      {
        path: 'factura',
        loadChildren: () => import('./routes/factura.routes').then((m) => m.routes),
        canActivate: [PermissionGuard],
        data: { pageTitle: 'factura.pageTitle', entity: EntityType.FACTURA }
      },
      {
        path: 'factura-owner',
        component: FacturaOwnerTableComponent,
        canActivate: [roleGuard([Role.ADMIN, Role.TOP_MANAGER, Role.OWNER])],
        data: { pageTitle: 'factura.pageTitle', entity: EntityType.FACTURA }
      },
      {
        path: 'setting',
        loadChildren: () => import('./features/setting/setting.routes').then((m) => m.routes),
        canActivate: [roleGuard([Role.ADMIN])],
        data: { pageTitle: 'setting.pageTitle' }
      },
      {
        path: 'translation',
        loadChildren: () => import('./routes/translation.routes').then((m) => m.routes),
        canActivate: [roleGuard([Role.ADMIN])],
        data: { pageTitle: 'translation.pageTitle', entity: EntityType.DYNAMICTRANSLATION }
      },
      {
        path: 'system-consistency',
        component: SystemConsistencyComponent,
        canActivate: [roleGuard([Role.ADMIN])],
        data: { pageTitle: 'System Consistency', entity: EntityType.SYSTEM_CONSISTENCY_FINDING }
      },
      { path: '', component: RootRedirectComponent, pathMatch: 'full' }
    ]
  },
  { path: '**', redirectTo: 'auth' }
];

if (environment.isDev) {
  routes
    .find((route) => route.path === '')
    .children?.push(
      ...[
        {
          path: 'paratype',
          loadChildren: () => import('src/app/routes/paratype.routes').then((m) => m.routes),
          canActivate: [roleGuard([Role.ADMIN])],
          data: { pageTitle: '---GENERATED---' }
        },
        {
          path: 'playground',
          loadChildren: () => import('./routes/playground.routes').then((m) => m.routes),
          canActivate: [roleGuard([Role.ADMIN])],
          data: { pageTitle: 'playground.pageTitle' }
        }
      ]
    );
}
